import { Provider } from "react-redux";
import {
  createBrowserRouter,
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider
} from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { Page } from "@components";
import { NotFoundState, NotificationsContainer, Spinner } from "@rio-cloud/rio-uikit";
import screens from "@screens";
import { persistor, store } from "@store";
import { theme } from "@theme";
import { PersistGate } from "redux-persist/integration/react";
import backoffice from "./backoffice";
import { LayoutType } from "./components/Page";
import AuthContextProvider from "./providers/AuthProvider";
import { NotificationsProvider } from "./providers/NotificationProvider";
import { WebSocketProvider } from "./providers/WebsocketProvider";
import { useAppSelector } from "./store/hook";
import "./styles/styles.css";
import api from "./services";
import { useEffect, useState } from "react";

const routingRoutesFromElements = createRoutesFromElements(
  <Route element={<Page />}>
    <Route path="/" element={<Navigate to="/routing/routes" replace />} />
    <Route path="/routing/map/trips" element={<screens.OnGoingTripsScreen />} />
    <Route path="/routing/trips" element={<screens.TripsScreen />} />
    <Route path="/routing/zone" element={<screens.AreasScreen />} />
    <Route path="/routing/routes" element={<screens.RoutesScreen />} />
    <Route path={"/routing/trips/smart-routes"} element={<screens.TripSmartRoute />} />
    <Route path="/routing/routes/create" element={<screens.RoutesCreateScreen />} />
    <Route path="/routing/routes/update" element={<screens.RoutesUpdateScreen />} />
    <Route path="/routing/trips/update" element={<screens.RoutesTripUpdate />} />
    <Route path="/routing/trips/performance" element={<screens.PerformanceScreen />} />
    <Route path="/routing/performance/list" element={<screens.PerformanceScreenList />} />
    <Route path="/routing/performance/list/trip" element={<screens.PerformanceScreen />} />
    <Route path="/routing/trips/routes" element={<screens.TripsListRouteScreen />} />
    <Route path="/routing/trips/driver" element={<screens.TripsDriverScreen />} />
    <Route path="/routing/trips/schedule" element={<screens.TripsSchedule />} />
    <Route path="/routing/trips/vehicle" element={<screens.TripsVehicleScreen />} />
    <Route path="/routing/notifications" element={<screens.NotificationsScreen />} />
    <Route path="/routing/map/fleets" element={<screens.CurrencyVehicleLocationScreen />} />
    {/* <Route path={"/routing/checkpoints"} element={<screens.CheckpointsScreen />} /> */}
    <Route path={"/routing/trips/custom"} element={<screens.RoutesTripUpdate />} />
    <Route path={"/routing/interest-points"} element={<screens.InterestPointsScreen />} />
    <Route path={"/routing/interest-points/create"} element={<screens.InterestPointsCreateScreen />} />
    <Route path={"/routing/interest-points/edit"} element={<screens.InterestPointsEditScreen />} />
    <Route path={"/routing/points"} element={<screens.PointsScreen />} />
  </Route>
);

// Rotas do backoffice (sem hash)
const backofficeRoutesFromElements = createRoutesFromElements(
  <Route element={<Page layoutType={LayoutType.BACKOFFICE} />}>
    <Route path="/backoffice/" element={<Navigate to="/backoffice/challenges" replace />} />
    <Route path="/backoffice/challenges" element={<backoffice.screens.ChallengesScreen />} />
    <Route path={"/backoffice/tips"} element={<backoffice.screens.NavigationTipsScreen />} />
  </Route>
);

export const routingRouter = createHashRouter(routingRoutesFromElements);
export const backofficeRouter = createBrowserRouter(backofficeRoutesFromElements);

// Componente que escolhe qual router usar baseado na URL
const AppRouter = () => {
  const isBackoffice = window.location.pathname.startsWith("/backoffice");
  // const hasBackofficeAccess = true; // userId && userAccessIds.includes(userId);
  const [access, setAccess] = useState<"loading" | "allowed" | "denied">("loading");

  useEffect(() => {
    if (!isBackoffice) return;
    api.get("/v1/routes/gamification/backoffice/access")
      .then(() => setAccess("allowed"))
      .catch(() => setAccess("denied"));
  }, [isBackoffice]);

  if (isBackoffice) {
    if (access === "loading") {
      return <div className="width-100pct height-100vh display-flex align-items-center justify-content-center"><Spinner/></div>
    }
    if (access === "denied") {
      return (
        <div className="width-100pct height-100vh display-flex align-items-center justify-content-center">
          <NotFoundState
            headline="Acesso negado"
            message="Você não tem permissão para acessar o backoffice."
            fullWidth
            outerClassName="border-none"
          />
        </div>
      );
    }
    return <RouterProvider router={backofficeRouter} />;
  }

  return <RouterProvider router={routingRouter} />;
};

const App = () => {
  return (
    <Provider store={store}>
      <AuthContextProvider>
        <PersistGate loading={null} persistor={persistor}>
          <NotificationsProvider>
            <WebSocketProvider>
              <ThemeProvider theme={theme}>
                <NotificationsContainer />
                <AppRouter />
              </ThemeProvider>
            </WebSocketProvider>
          </NotificationsProvider>
        </PersistGate>
      </AuthContextProvider>
    </Provider>
  );
};

export default App;
